import {
  AutoCompleteSearchInput,
  ConfirmationModal,
  getHighlightedText
} from '@nile/components';
import {
  Button,
  ButtonGroup,
  DisplayText,
  Heading,
  ResourceItem,
  ResourceList,
  TextStyle,
  Checkbox
} from '@superops/ui-design-system';
import React, { useCallback, useState } from 'react';
import styled from 'styled-components';
import { ReactSortable } from 'react-sortablejs';
import { FormattedMessage } from 'react-intl';
import { SVGIcon } from '@superops/super-icons';

function SelectionSheet(props) {
  const {
    sections,
    meta,
    actions,
    template,
    canReorder,
    isPromptConfirmation,
    className,
    allowSameItemSelection,
    dynamicHeight,
    noCheckbox,
    noSearchBar
  } = props;
  const [selectedItems, setSelectedItems] = useState(props.selectedItems || []);
  const [list, setList] = useState(props.selectedItems || []);
  const listByRef = React.useRef(1);
  listByRef.current = list;
  const [selectedItemsReference, setSelectedItemsReference] = useState({});
  const [filterExpression, setFilterExpression] = useState('');

  /**
   * Default render for selected column fields
   * @param {Array} selectedItems
   * @param {Object} item
   * @returns {ReactNode}
   */
  function renderdDefaultSelectedItem(selectedItems, item) {
    const { label, id } = item;
    const selectedIds = selectedItems.map((el) => el.id);
    return (
      <ResourceItem
        id={item.id}
        accessibilityLabel="sample"
        onClick={() => {
          if (item.disable) return;
          selectedIds.indexOf(item.id) !== -1 &&
            selectedIds.splice(selectedIds.indexOf(item.id), 1);
          handleUnselection(selectedIds);
        }}
      >
        {template ? (
          <>
            {noCheckbox ? (
              <span className="text-with-no-checkbox">{label}</span>
            ) : (
              <div className="flex flex-align-center negativeIndex">
                <Checkbox
                  id={id}
                  name={id}
                  checked
                  disabled={item.disable}
                  onChange={(checked, value) => {
                    selectedIds.splice(selectedIds.indexOf(value), 1);
                    handleUnselection(selectedIds);
                  }}
                />
                {template(item)}
              </div>
            )}
          </>
        ) : (
          <TextStyle>
            {noCheckbox ? (
              <span className="text-with-no-checkbox">{label}</span>
            ) : (
              <div className="negativeIndex">
                <Checkbox
                  label={<TextStyle>{label}</TextStyle>}
                  id={id}
                  name={id}
                  checked
                  onChange={(checked, value) => {
                    selectedIds.splice(selectedIds.indexOf(value), 1);
                    handleUnselection(selectedIds);
                  }}
                />
              </div>
            )}
          </TextStyle>
        )}
      </ResourceItem>
    );
  }

  function renderItem(type, sectionIndex, item) {
    const { label, id } = item;
    return (
      <ResourceItem
        id={item.id}
        accessibilityLabel={`sample`}
        onClick={() => {
          if (item.disable) return;
          handleSelectedItems(sectionIndex, [id]);
        }}
      >
        <div data-qa={item.id} superid={item.id}>
          <DisplayText element="h3">
            {template ? (
              <>
                {noCheckbox ? (
                  <span className="text-with-no-checkbox">{label}</span>
                ) : (
                  <div className="flex flex-align-center negativeIndex">
                    <Checkbox
                      id={id}
                      name={id}
                      disabled={item.disable}
                      onChange={() => handleSelectedItems(sectionIndex, [id])}
                    />
                    {template(item)}
                  </div>
                )}
              </>
            ) : filterExpression && type === 'available' ? (
              <TextStyle>
                {noCheckbox ? (
                  <span className="text-with-no-checkbox">{label}</span>
                ) : (
                  <div className="negativeIndex">
                    <Checkbox
                      label={
                        <TextStyle>
                          {getHighlightedText(label, filterExpression)}
                        </TextStyle>
                      }
                      id={id}
                      name={id}
                      onChange={() => handleSelectedItems(sectionIndex, [id])}
                    />
                  </div>
                )}
              </TextStyle>
            ) : (
              <TextStyle>
                {noCheckbox ? (
                  <span className="text-with-no-checkbox">{label}</span>
                ) : (
                  <div className="negativeIndex">
                    <Checkbox
                      label={<TextStyle>{label}</TextStyle>}
                      id={id}
                      name={id}
                      onChange={() => handleSelectedItems(sectionIndex, [id])}
                    />
                  </div>
                )}
              </TextStyle>
            )}
          </DisplayText>
        </div>
      </ResourceItem>
    );
  }

  function renderSelectedItem(type, item, currentLi) {
    const { name, label } = item;
    const handleRemoval = (i, currentList) => {
      const updatedSelectedItems = listByRef.current.filter(
        (el) => el.id !== i.id
      );
      setList(updatedSelectedItems);
      const updatedIds = updatedSelectedItems.map((el) => el.id);
      handleUnselection(updatedIds);
    };
    return (
      <ResourceItem
        id={item.id}
        accessibilityLabel={`sample`}
        onClick={() => {
          if (item.disable) return;
          selectedIds.splice(selectedIds.indexOf(item.id), 1);
          handleUnselection(selectedIds);
        }}
      >
        <div data-qa={item.id} superid={item.id}>
          <DisplayText element="h3">
            {template ? (
              <>
                {noCheckbox ? (
                  <span className="text-with-no-checkbox">{label}</span>
                ) : (
                  <div className="negativeIndex">
                    <Checkbox
                      label={template(item)}
                      id={label}
                      name={label}
                      checked
                      disabled={item.disable}
                      onChange={handleRemoval.bind(this, item, currentLi)}
                    />
                  </div>
                )}
              </>
            ) : filterExpression && type === 'available' ? (
              <TextStyle>
                {getHighlightedText(label, filterExpression)}
              </TextStyle>
            ) : (
              <TextStyle>
                <div className="flex center">
                  <div
                    className="fx center dragHandle mr8 cursor-move"
                    style={{ opacity: '0.5' }}
                  >
                    <SVGIcon name="Reorder" />
                  </div>
                  {noCheckbox ? (
                    <span className="text-with-no-checkbox">{label}</span>
                  ) : (
                    <div className="negativeIndex">
                      <Checkbox
                        label={label}
                        id={label}
                        name={label}
                        checked
                        disabled={item.disable}
                        onChange={handleRemoval.bind(this, item, currentLi)}
                      />
                    </div>
                  )}
                </div>
              </TextStyle>
            )}
          </DisplayText>
        </div>
      </ResourceItem>
    );
  }

  const handleSelectedItems = (sectionIndex, selectedItem) => {
    let selectedSectionItems = sections[sectionIndex].items.find(
      (el) => el.id === selectedItem[0]
    );
    const newSelectedItems = selectedItems.concat([selectedSectionItems]);
    setSelectedItems(newSelectedItems);
    setList(newSelectedItems);
    setSelectedItemsReference({
      ...selectedItemsReference,
      [sectionIndex]: selectedItem
    });
  };

  const handleUnselection = (remainingSelectedItems) => {
    if (remainingSelectedItems.length) {
      setSelectedItems(
        listByRef.current.filter((el) => {
          return remainingSelectedItems.some((item) => item === el.id);
        })
      );
    } else {
      setSelectedItems([]);
    }
  };

  const updateParent = () => {
    if (props.toggleSheetActive) props.toggleSheetActive();
    else actions.toggleSheetActive();
    props.updateParent(selectedItems);
  };

  const handleDone = () => {
    requestAnimationFrame(updateParent);
  };

  const debouncedFilter = useCallback((value) => {
    setFilterExpression(value.toLowerCase());
  });

  const stopEvents = (e) => {
    e.stopPropagation();
    e.preventDefault();
  };

  const associateModalActivator = (
    <Button primary>
      <FormattedMessage id="admin.Technicianrole.done" />
    </Button>
  );

  const getHoverconfig = () => {
    const { hoverConfig } = props;
    if (hoverConfig) {
      return hoverConfig;
    }
    return {
      background: 'rgba(0, 0, 0, 0.04)'
    };
  };

  const hasContent =
    (sections?.length > 0 && sections.filter((el) => el.items?.length > 0))
      ?.length > 0 || selectedItems?.length > 0;
  return (
    <SelectionWrapper
      className={`vw30 vh100 selection-sheet ${className || ''}`}
      onClick={stopEvents}
      hoverConfig={getHoverconfig()}
      dynamicHeight={dynamicHeight}
    >
      <div className="flex fcol">
        <div
          className="flex"
          data-qa="selectinSheetDoneBtn"
          superid="selectinSheetDoneBtn"
        >
          <Heading element="h1">{meta.heading}</Heading>
          <div className="fx mlauto">
            <ButtonGroup>
              {isPromptConfirmation ? (
                <ConfirmationModal
                  modalActivator={associateModalActivator}
                  description={
                    'On associating the selected employees, the previously assigned roles will be revoked.'
                  }
                  handleConfirmation={handleDone}
                  title={'Associate Users'}
                />
              ) : (
                <Button primary onClick={handleDone}>
                  <FormattedMessage id="admin.Technicianrole.done" />
                </Button>
              )}
            </ButtonGroup>
          </div>
        </div>

        {hasContent && !noSearchBar && (
          <AutoCompleteSearchInput debouncedFilter={debouncedFilter} />
        )}

        <div
          className={`${dynamicHeight ? '' : 'scroll-in '} ${noSearchBar &&
            'mt-20'}`}
        >
          {selectedItems.length > 0 && (
            <div className="mb10 gen" name={'selected-items'}>
              <Heading element="h3" weight="Bold">
                {props?.meta?.selectedTitle || 'Selected items'}
              </Heading>
              {canReorder ? (
                <ReactSortable
                  className="sort-wrapper"
                  list={list}
                  handle=".dragHandle"
                  setList={(li) => {
                    setList(li);
                    setSelectedItems(li);
                  }}
                  animation={200}
                >
                  {list.map((item) => (
                    <div key={item.id} className="ptr">
                      <TextStyle>
                        {renderSelectedItem('selected', item, selectedItems)}
                      </TextStyle>
                    </div>
                  ))}
                </ReactSortable>
              ) : (
                <ResourceList
                  items={selectedItems}
                  renderItem={renderdDefaultSelectedItem.bind(
                    this,
                    selectedItems
                  )}
                />
              )}
            </div>
          )}
          {sections.map((section, sectionIndex) => {
            const renderItems = section.items.filter((el) => {
              if (filterExpression) {
                // Don't filter selected items if allow same item selection is supported.
                if (allowSameItemSelection) {
                  return (
                    el.label &&
                    el.label.toLowerCase().indexOf(filterExpression) > -1
                  );
                }
                return (
                  el.label &&
                  el.label.toLowerCase().indexOf(filterExpression) > -1 &&
                  !selectedItems.some((item) => item.id === el.id)
                );
              }
              // Don't filter selected items if allow same item selection is supported.
              return (
                allowSameItemSelection ||
                !selectedItems.some((item) => item.id === el.id)
              );
            });
            return (
              <SectionHolder
                dynamicHeight={dynamicHeight}
                selectedItemsHeight={selectedItems?.length * 50 || 0}
                name={section.header}
                className="flex fcol mb10 mt-20"
              >
                <Heading element="h3" weight="Bold">
                  {section.header}
                </Heading>
                {section.component || ''}
                <div className="selection-sheet-list">
                  <ResourceList
                    items={renderItems}
                    renderItem={renderItem.bind(
                      this,
                      'available',
                      sectionIndex
                    )}
                  />
                </div>
                {renderItems.length === 0 ? (
                  <div>
                    {' '}
                    <FormattedMessage id="admin.Technicianrole.associate.nodata" />
                  </div>
                ) : (
                  ''
                )}
              </SectionHolder>
            );
          })}
        </div>
      </div>
    </SelectionWrapper>
  );
}

const SectionHolder = styled.div`
  height: ${(props) =>
    props.dynamicHeight
      ? `calc(100vh - ${props.selectedItemsHeight + 223}px)`
      : 'auto'};
  overflow: ${(props) => (props.dynamicHeight ? 'auto' : '')};
`;

const SelectionWrapper = styled.div`
  padding: 12px;
  overflow: ${(props) => (props.dynamicHeight ? 'hidden' : 'auto')};

  .text-with-no-checkbox {
    color: rgba(0, 0, 0, 0.8) !important
    font-weight: 500;
    font-size: 14px;
  }
  .Search-Input {
    margin-left: 0px !important;
    margin-right: 0px !important;
  }
  .negativeIndex{
    .Superops-Checkbox__Backdrop {
      z-index: -1;
    }
  }

`;

export default SelectionSheet;
